import type { MessageSummary } from '@/api/types';

/**
 * The identity of a row *in the list on screen* (ADR 0056).
 *
 * A message id embeds no account (ADR 0012): it is
 * `base64url("<uidvalidity>:<uid>:<mailbox>")`, and two mailboxes on the same
 * server routinely mint the same string. In a single-account list that never
 * mattered — every row was one account's. In the merged list it matters three
 * times over, and all three are silent corruptions rather than crashes:
 *
 * - the **selection** is a set of ids, so ticking a row in one mailbox would
 *   untick its twin in another;
 * - the **keyboard cursor** is an id resolved with `findIndex`, so `Enter`
 *   would open whichever mailbox's copy happened to be higher up;
 * - the **virtualizer key** is an id, so React would see two rows with one key
 *   and reuse the wrong DOM node as the list scrolled.
 *
 * So everything that identifies a row on screen uses this instead. `\u0000`
 * cannot occur in a base64url id or in an account id, so the pair round-trips
 * unambiguously.
 */
export function rowKey(
  message: Pick<MessageSummary, 'accountId' | 'id'> & { conversation?: { id: string } },
): string {
  // A conversation row (ADR 0077) is its conversation, not its head: new mail
  // changes the head, and the cursor and the selection must not notice.
  if (message.conversation !== undefined) {
    return conversationRowKey(message.accountId, message.conversation.id);
  }
  return `${message.accountId}\u0000${message.id}`;
}

/**
 * A conversation row's key. `c:` cannot begin a message id (base64url has no
 * colon), so a conversation and a message never share a key.
 */
export function conversationRowKey(accountId: string, conversationId: string): string {
  return `${accountId}\u0000c:${conversationId}`;
}

/** The same key for a row that is only known as (account, message) ids. */
export function rowKeyOf(accountId: string | undefined, messageId: string | undefined): string {
  return `${accountId ?? ''}\u0000${messageId ?? ''}`;
}
